import { ChangeEvent, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import {
    DROPDOWN_NAMES,
    DropdownValue,
    EditInlineDialogProps,
    INPUT_NAMES
} from "components/Actions/dialogs/types";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import {
    ENDPOINTS,
    useApi,
    useMediaQueries,
    useStringInputValidation
} from "shared";

import { Value } from "../types";

const EditFileInline = ({ data, isOpen, close }: EditInlineDialogProps) => {
    const { t } = useTranslation();
    const { updateData, handleResponse } = useApi();
    const { toMd } = useMediaQueries();

    const [isLoading, setIsLoading] = useState(false);

    const [value, setValue] = useState<Value>({
        [INPUT_NAMES.DescriptionValue]: "",
        [DROPDOWN_NAMES.CompanyValue]: null
    });

    const { descriptionValue, companyValue } = value;

    const { stringInputError } = useStringInputValidation(descriptionValue);

    useEffect(() => {
        if (data) {
            const {
                description,
                company: { id: companyId, name: companyName }
            } = data;

            setValue({
                [INPUT_NAMES.DescriptionValue]: description,
                [DROPDOWN_NAMES.CompanyValue]: {
                    id: companyId,
                    name: companyName
                }
            });
        }
    }, [data]);

    const handleSubmit = async () => {
        const payload = {
            description: descriptionValue,
            company_id: companyValue?.id
        };

        try {
            setIsLoading(true);

            const response = await updateData(
                ENDPOINTS.Files,
                data.id,
                payload
            );

            close();
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) =>
        setValue({ ...value, [e.target.name]: e.target.value });

    const handleDropdownChange = (
        dropdownName: DROPDOWN_NAMES,
        dropdownValue: DropdownValue | null
    ) => setValue({ ...value, [dropdownName]: dropdownValue });

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator
            title={t("General##edit file")}
            open={isOpen}
            close={close}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setValue({
                            [INPUT_NAMES.DescriptionValue]: "",
                            [DROPDOWN_NAMES.CompanyValue]: null
                        });
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={!stringInputError}
                    cancelButtonTestId="cancel-edit-file-inline"
                    confirmationButtonTestId="save-edit-file-inline"
                />
            }
            extendToMaxWidth={toMd}
        >
            <CompanyDropdown
                data-testid="edit-file-company-dropdown"
                value={companyValue}
                readOnly={isLoading}
                change={(_, val) =>
                    handleDropdownChange(DROPDOWN_NAMES.CompanyValue, val)
                }
                customStyle={{ margin: "8px 0 16px 0" }}
                isRootRequired
            />

            <InputField
                data-testid="edit-user-description-input"
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                readOnly={isLoading}
                name={INPUT_NAMES.DescriptionValue}
                value={descriptionValue}
                onChange={handleInputChange}
                labelLeft={t("Dialog##description")}
                placeholder={t("Dialog##enter description")}
                customStyle={{ marginBottom: "16px" }}
                errorText={stringInputError}
            />
        </Dialog>
    );
};

export default EditFileInline;
